import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import './AdminCategoryManager.css';

const AdminCategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });

  // Form states
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Form inputs
  const [categoryName, setCategoryName] = useState('');
  const [categoryDesc, setCategoryDesc] = useState('');
  const [subcategoryName, setSubcategoryName] = useState('');
  const [subcategoryDesc, setSubcategoryDesc] = useState('');

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Clear alerts after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/categories');
      setCategories(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load categories';
      console.error('Error fetching categories:', err);
      setError(errorMsg);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      const payload = {
        name: categoryName,
        description: categoryDesc || null,
      };

      if (editingCategory) {
        await axios.put(`/api/categories/${editingCategory.id}`, payload);
        setSuccess('Category updated successfully');
      } else {
        await axios.post('/api/categories', payload);
        setSuccess('Category created successfully');
      }

      setCategoryName('');
      setCategoryDesc('');
      setEditingCategory(null);
      setShowCategoryForm(false);
      fetchCategories();
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save category');
      console.error('Error saving category:', err);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setCategoryDesc(category.description || '');
    setShowCategoryForm(true);
  };

  const openConfirmDialog = (title, message, confirmText, onConfirm) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    openConfirmDialog(
      'Delete Category',
      'Are you sure you want to delete this category? All subcategories will also be deleted.',
      'Delete',
      async () => {
        try {
          await axios.delete(`/api/categories/${categoryId}`);
          setSuccess('Category deleted successfully');
          fetchCategories();
          setError('');
        } catch (err) {
          setError(err.response?.data?.detail || 'Failed to delete category');
          console.error('Error deleting category:', err);
        }
      }
    );
  };

  // Subcategory handlers
  const handleAddSubcategory = async (e) => {
    e.preventDefault();
    if (!selectedCategory) {
      setError('Please select a category first');
      return;
    }
    if (!subcategoryName.trim()) {
      setError('Subcategory name is required');
      return;
    }

    try {
      const payload = {
        name: subcategoryName,
        description: subcategoryDesc || null,
      };

      if (editingSubcategory) {
        await axios.put(`/api/categories/subcategories/${editingSubcategory.id}`, payload);
        setSuccess('Subcategory updated successfully');
      } else {
        await axios.post(`/api/categories/${selectedCategory}/subcategories`, payload);
        setSuccess('Subcategory created successfully');
      }

      setSubcategoryName('');
      setSubcategoryDesc('');
      setEditingSubcategory(null);
      setShowSubcategoryForm(false);
      fetchCategories();
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save subcategory');
      console.error('Error saving subcategory:', err);
    }
  };

  const handleEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryName(subcategory.name);
    setSubcategoryDesc(subcategory.description || '');
    setShowSubcategoryForm(true);
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    openConfirmDialog('Delete Subcategory', 'Are you sure you want to delete this subcategory?', 'Delete', async () => {
      try {
        await axios.delete(`/api/categories/subcategories/${subcategoryId}`);
        setSuccess('Subcategory deleted successfully');
        fetchCategories();
        setError('');
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to delete subcategory');
        console.error('Error deleting subcategory:', err);
      }
    });
  };

  const handleCancelCategoryForm = () => {
    setCategoryName('');
    setCategoryDesc('');
    setEditingCategory(null);
    setShowCategoryForm(false);
  };

  const handleCancelSubcategoryForm = () => {
    setSubcategoryName('');
    setSubcategoryDesc('');
    setEditingSubcategory(null);
    setShowSubcategoryForm(false);
  };

  const handleSelectCategory = (categoryId) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
  };

  if (loading) {
    return (
      <div className="admin-category-container">
        <div className="header">
          <h1>Category Management</h1>
        </div>
        <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: '1.1rem', color: '#667eea' }}>
          Loading categories...
        </div>
      </div>
    );
  }

  const currentCategory = categories.find(cat => cat.id === selectedCategory);

  return (
    <div className="admin-category-container">
      <div className="header">
        <h1>Category Management</h1>
        <p>Manage inventory categories and subcategories</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="content-layout">
        {/* Categories List */}
        <div className="categories-section">
          <div className="section-header">
            <h2>Categories</h2>
            <button
              className="btn btn-primary"
              onClick={() => {
                handleCancelCategoryForm();
                setShowCategoryForm(true);
              }}
            >
              + Add Category
            </button>
          </div>

          {showCategoryForm && (
            <form className="form-section" onSubmit={handleAddCategory}>
              <h3>{editingCategory ? 'Edit Category' : 'New Category'}</h3>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  placeholder="Enter category name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={categoryDesc}
                  onChange={(e) => setCategoryDesc(e.target.value)}
                  placeholder="Enter category description (optional)"
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success">
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelCategoryForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="categories-list">
            {categories.length === 0 ? (
              <div className="empty-state">No categories yet. Create one to get started!</div>
            ) : (
              categories.map((category) => (
                <div
                  key={category.id}
                  className={`category-item ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => handleSelectCategory(category.id)}
                >
                  <div className="category-info">
                    <h3>{category.name}</h3>
                    <p className="category-count">
                      {category.subcategories?.length || 0} subcategories
                    </p>
                    {category.description && <p className="category-desc">{category.description}</p>}
                  </div>
                  <div className="category-actions">
                    <button
                      className="btn btn-small btn-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCategory(category);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-small btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(category.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Subcategories List */}
        <div className="subcategories-section">
          {currentCategory ? (
            <>
              <div className="section-header">
                <h2>Subcategories - {currentCategory.name}</h2>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleCancelSubcategoryForm();
                    setShowSubcategoryForm(true);
                  }}
                >
                  + Add Subcategory
                </button>
              </div>

              {showSubcategoryForm && (
                <form className="form-section" onSubmit={handleAddSubcategory}>
                  <h3>{editingSubcategory ? 'Edit Subcategory' : 'New Subcategory'}</h3>
                  <div className="form-group">
                    <label>Subcategory Name *</label>
                    <input
                      type="text"
                      value={subcategoryName}
                      onChange={(e) => setSubcategoryName(e.target.value)}
                      placeholder="Enter subcategory name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={subcategoryDesc}
                      onChange={(e) => setSubcategoryDesc(e.target.value)}
                      placeholder="Enter subcategory description (optional)"
                      rows="3"
                    />
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-success">
                      {editingSubcategory ? 'Update Subcategory' : 'Create Subcategory'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCancelSubcategoryForm}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              <div className="subcategories-list">
                {currentCategory.subcategories && currentCategory.subcategories.length === 0 ? (
                  <div className="empty-state">No subcategories yet. Add one to organize products!</div>
                ) : (
                  currentCategory.subcategories?.map((subcategory) => (
                    <div key={subcategory.id} className="subcategory-item">
                      <div className="subcategory-info">
                        <h4>{subcategory.name}</h4>
                        {subcategory.description && <p className="subcategory-desc">{subcategory.description}</p>}
                        <small className="subcategory-id">ID: {subcategory.id}</small>
                      </div>
                      <div className="subcategory-actions">
                        <button
                          className="btn btn-small btn-edit"
                          onClick={() => handleEditSubcategory(subcategory)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-small btn-delete"
                          onClick={() => handleDeleteSubcategory(subcategory.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>Select a category to manage its subcategories</p>
            </div>
          )}
        </div>
      </div>

      {confirmDialog.show && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>{confirmDialog.title || 'Confirm Action'}</h2>
              <button className="close-btn" onClick={closeConfirmDialog}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 0 }}>{confirmDialog.message}</p>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={closeConfirmDialog}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={executeConfirmDialogAction}>
                {confirmDialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategoryManager;
